class BBSProductPage {
  static FADE_MS = 300;

  constructor(root) {
    this.root = root;
    this.product = null;
    this.selectedOptions = {};
    this.currentVariant = null;
    this.imageSwapToken = 0;

    try {
      const jsonEl = root.querySelector('[data-bbs-product-json]');
      this.product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    } catch (error) {
      console.error('BBS product JSON parse failed', error);
    }

    this.form = root.querySelector('[data-bbs-product-form]');
    this.variantInput = root.querySelector('[data-bbs-variant-id]');
    this.mainImage = root.querySelector('[data-bbs-main-image]');
    this.mediaZoomBtn = root.querySelector('[data-bbs-media-zoom]');
    this.priceEl = root.querySelector('[data-bbs-price]');
    this.comparePriceEl = root.querySelector('[data-bbs-compare-price]');
    this.submitBtn = root.querySelector('[data-bbs-add-to-cart]');
    this.thumbs = root.querySelectorAll('[data-bbs-thumb]');
    this.swatchButtons = root.querySelectorAll('[data-bbs-product-swatch]');
    this.optionSelects = root.querySelectorAll('[data-bbs-option-select]');
    this.personaliseToggle = root.querySelector('[data-bbs-personalise-toggle]');
    this.personaliseFields = root.querySelector('[data-bbs-personalise-fields]');
    this.personaliseInputs = root.querySelectorAll('[data-bbs-personalise-input]');
    this.personaliseTerms = root.querySelector('[data-bbs-personalise-terms]');
    this.quantityInput = root.querySelector('[data-bbs-quantity-input]');
    this.quantityMinus = root.querySelector('[data-bbs-quantity-minus]');
    this.quantityPlus = root.querySelector('[data-bbs-quantity-plus]');
    this.shareToast = document.querySelector('[data-bbs-share-toast]');
    this.variantPricing = {};

    try {
      const pricingEl = root.querySelector('[data-bbs-variant-pricing]');
      if (pricingEl) {
        this.variantPricing = JSON.parse(pricingEl.textContent);
      }
    } catch (error) {
      console.error('BBS variant pricing JSON parse failed', error);
    }

    this.initSelectedOptions();
    this.bindGallery();
    this.bindSwatches();
    this.bindOptionSelects();
    this.bindPersonalisation();
    this.bindShare();
    this.bindDescription();
    this.bindQuantity();
    this.bindForm();
    this.refreshVariantSelection();
    this.trackRecentlyViewed();
  }

  isPersonalisationOptionName(name) {
    const normalized = name?.toLowerCase().trim();
    return (
      normalized === 'personalised' ||
      normalized === 'personalized' ||
      normalized === 'personalization' ||
      normalized === 'personalisation'
    );
  }

  getPersonalisationOptionName() {
    if (!this.product?.options?.length) return null;
    return this.product.options.find((name) => this.isPersonalisationOptionName(name)) || null;
  }

  getPersonalisationValues(optionName) {
    const index = this.product.options.indexOf(optionName);
    if (index === -1) {
      return { no: 'No', yes: 'Yes' };
    }

    const key = `option${index + 1}`;
    const values = [
      ...new Set(
        this.product.variants
          .map((variant) => variant[key])
          .filter((value) => value != null && value !== '')
      ),
    ];

    const noVal = values.find((value) => /^no$/i.test(value)) || values[0] || 'No';
    const yesVal =
      values.find((value) => /^yes$/i.test(value)) ||
      values.find((value) => value !== noVal) ||
      values[1] ||
      'Yes';

    return { no: noVal, yes: yesVal };
  }

  syncPersonalisationVariant() {
    const optionName = this.getPersonalisationOptionName();
    if (!optionName || !this.personaliseToggle) return;

    const { no, yes } = this.getPersonalisationValues(optionName);
    this.selectedOptions[optionName] = this.personaliseToggle.checked ? yes : no;
    this.refreshVariantSelection();
  }

  getColourOptionName() {
    const swatch = this.swatchButtons[0];
    return swatch?.dataset.optionName || null;
  }

  buildOptionsForSelection(overrides = {}) {
    const options = { ...this.selectedOptions, ...overrides };
    const personalisationOptionName = this.getPersonalisationOptionName();

    if (personalisationOptionName && this.personaliseToggle) {
      const { no, yes } = this.getPersonalisationValues(personalisationOptionName);
      options[personalisationOptionName] = this.personaliseToggle.checked ? yes : no;
    }

    return options;
  }

  findVariantForOptions(options) {
    if (!this.product?.options?.length) {
      return this.product?.variants?.[0] || null;
    }

    return (
      this.product.variants.find((variant) =>
        this.product.options.every((name, index) => {
          const key = `option${index + 1}`;
          const selected = options[name];
          if (selected == null || selected === '') return true;
          return variant[key] === selected;
        })
      ) || null
    );
  }

  isOptionValueAvailable(optionName, optionValue) {
    const options = this.buildOptionsForSelection({ [optionName]: optionValue });
    return Boolean(this.findVariantForOptions(options));
  }

  updateSwatchAvailability() {
    const colourOptionName = this.getColourOptionName();
    if (!colourOptionName) return;

    this.swatchButtons.forEach((button) => {
      if (button.dataset.optionName !== colourOptionName) return;

      const available = this.isOptionValueAvailable(colourOptionName, button.dataset.value);
      button.hidden = !available;
      button.disabled = !available;
    });

    this.optionSelects.forEach((select) => {
      if (select.dataset.optionName !== colourOptionName) return;

      Array.from(select.options).forEach((option) => {
        const available = this.isOptionValueAvailable(colourOptionName, option.value);
        option.hidden = !available;
        option.disabled = !available;
      });
    });
  }

  ensureValidColourSelection() {
    const colourOptionName = this.getColourOptionName();
    if (!colourOptionName) return;

    const current = this.selectedOptions[colourOptionName];
    if (current && this.isOptionValueAvailable(colourOptionName, current)) return;

    const availableButton = Array.from(this.swatchButtons).find(
      (button) =>
        button.dataset.optionName === colourOptionName &&
        !button.hidden &&
        this.isOptionValueAvailable(colourOptionName, button.dataset.value)
    );

    if (availableButton) {
      this.selectedOptions[colourOptionName] = availableButton.dataset.value;
      this.syncOptionSelect(colourOptionName, availableButton.dataset.value);
      this.updateSwatchStates();
    }
  }

  refreshVariantSelection() {
    this.updateSwatchAvailability();
    this.ensureValidColourSelection();
    this.updateVariant();
  }

  isQuickView() {
    return Boolean(this.root.closest('[data-bbs-quick-view-modal]') || this.root.hasAttribute('data-bbs-quick-view-content'));
  }

  initSelectedOptions() {
    if (!this.product?.options?.length) return;

    const renderedVariantId = this.variantInput?.value;
    const urlVariantId = this.isQuickView()
      ? null
      : new URLSearchParams(window.location.search).get('variant');

    let baseVariant = null;

    if (renderedVariantId) {
      baseVariant = this.product.variants.find((variant) => String(variant.id) === String(renderedVariantId));
    }

    if (!baseVariant && urlVariantId) {
      baseVariant = this.product.variants.find((variant) => String(variant.id) === String(urlVariantId));
    }

    if (!baseVariant) {
      baseVariant =
        this.product.variants.find((variant) => variant.available) ||
        this.product.variants[0];
    }

    if (!baseVariant) return;

    this.product.options.forEach((name, index) => {
      const key = `option${index + 1}`;
      const value = baseVariant[key];
      if (value != null && value !== '') {
        this.selectedOptions[name] = value;
      }
    });

    this.optionSelects.forEach((select) => {
      const optionName = select.dataset.optionName;
      if (this.selectedOptions[optionName]) {
        select.value = this.selectedOptions[optionName];
      }
    });

    this.updateSwatchStates();
  }

  bindGallery() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        if (!this.mainImage || !src) return;

        this.swapMainImage({ src, srcset, mediaId });

        this.thumbs.forEach((item) => {
          item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
        });
      });
    });
  }

  bindSwatches() {
    this.swatchButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const optionName = button.dataset.optionName;
        const value = button.dataset.value;
        if (!optionName || !value) return;

        this.selectedOptions[optionName] = value;
        this.syncOptionSelect(optionName, value);
        this.updateSwatchStates();
        this.refreshVariantSelection();
      });
    });
  }

  bindOptionSelects() {
    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        const optionName = select.dataset.optionName;
        this.selectedOptions[optionName] = select.value;
        this.updateSwatchStates();
        this.refreshVariantSelection();
      });
    });
  }

  syncOptionSelect(optionName, value) {
    this.optionSelects.forEach((select) => {
      if (select.dataset.optionName === optionName) {
        select.value = value;
      }
    });
  }

  updateSwatchStates() {
    this.swatchButtons.forEach((button) => {
      const optionName = button.dataset.optionName;
      const isActive = this.selectedOptions[optionName] === button.dataset.value;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  findVariant() {
    return this.findVariantForOptions(this.buildOptionsForSelection());
  }

  updateVariant() {
    this.currentVariant = this.findVariant();

    if (!this.currentVariant) {
      if (this.variantInput) {
        this.variantInput.value = '';
        this.variantInput.disabled = true;
      }

      this.updateSubmitState();
      return;
    }

    if (this.variantInput) {
      this.variantInput.disabled = false;
      this.variantInput.value = this.currentVariant.id;
      if (!this.currentVariant.available) {
        this.variantInput.disabled = true;
      }
    }

    this.updatePricing();
    this.updateSubmitState();
    this.updateGalleryForVariant();
    this.updateUrl();
  }

  getVariantCents(variant) {
    if (!variant) {
      return { price: 0, compareAt: 0 };
    }

    const pricing = this.variantPricing[String(variant.id)] || {};
    const price = Number(pricing.price ?? variant.price) || 0;
    const compareAt = Number(pricing.compare_at_price ?? variant.compare_at_price) || 0;

    return { price, compareAt };
  }

  updatePricing() {
    if (!this.currentVariant) return;

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      return `£${(cents / 100).toFixed(2)}`;
    };

    const { price, compareAt } = this.getVariantCents(this.currentVariant);
    const onSale = compareAt > price;

    if (this.priceEl) {
      this.priceEl.textContent = formatMoney(price);
      this.priceEl.classList.toggle('bbs-product__price--sale', onSale);
    }

    if (this.comparePriceEl) {
      if (onSale) {
        this.comparePriceEl.textContent = formatMoney(compareAt);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }
  }

  updateSubmitState() {
    if (!this.submitBtn) return;

    const hasVariant = Boolean(this.currentVariant);
    const available = hasVariant && this.currentVariant.available !== false;
    this.submitBtn.disabled = !available;

    const label = this.submitBtn.querySelector('span');
    let labelText = 'Sold Out';
    if (hasVariant && available) {
      labelText = this.submitBtn.dataset.labelAdd || 'Add to Basket';
    } else if (!hasVariant) {
      labelText = 'Unavailable';
    }

    if (label) {
      label.textContent = labelText;
    } else {
      this.submitBtn.textContent = labelText;
    }
  }

  getSelectedColour() {
    const colourSwatch = this.root.querySelector('[data-bbs-product-swatch]');
    if (colourSwatch?.dataset.optionName) {
      return this.selectedOptions[colourSwatch.dataset.optionName] || null;
    }

    return this.selectedOptions.Colour || this.selectedOptions.Color || null;
  }

  normalizeImageSrc(src) {
    if (!src) return '';

    try {
      const absolute = src.startsWith('//') ? `https:${src}` : src;
      const url = new URL(absolute, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return src.split('?')[0];
    }
  }

  imageFileKey(src) {
    if (!src) return '';

    const normalized = this.normalizeImageSrc(src);
    const parts = normalized.split('/').filter(Boolean);
    return (parts[parts.length - 1] || normalized).toLowerCase();
  }

  imageSrcMatches(left, right) {
    if (!left || !right) return false;

    if (this.normalizeImageSrc(left) === this.normalizeImageSrc(right)) {
      return true;
    }

    return this.imageFileKey(left) === this.imageFileKey(right);
  }

  getSwatchForColour(colour) {
    if (!colour) return null;

    return (
      Array.from(this.swatchButtons).find((button) => button.dataset.value === colour) || null
    );
  }

  preloadImage(src, srcset) {
    return new Promise((resolve) => {
      const img = new Image();
      const done = () => resolve();

      img.onload = done;
      img.onerror = done;

      if (srcset) {
        img.srcset = srcset.trim();
      }
      img.src = src;

      if (img.complete && img.naturalWidth > 0) {
        done();
      }
    });
  }

  waitForImageElement(img) {
    if (img.complete && img.naturalWidth > 0) {
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => {});
      }
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }

  swapMainImage({ src, srcset, mediaId }) {
    if (!this.mainImage || !src) return Promise.resolve();

    const currentSrc = this.mainImage.currentSrc || this.mainImage.src;
    if (currentSrc && this.imageSrcMatches(src, currentSrc)) {
      return Promise.resolve();
    }

    const token = ++this.imageSwapToken;
    const trimmedSrcset = srcset?.trim() || '';

    this.mainImage.classList.add('is-swapping');

    return Promise.all([
      this.preloadImage(src, trimmedSrcset),
      new Promise((resolve) => window.setTimeout(resolve, BBSProductPage.FADE_MS)),
    ]).then(() => {
      if (token !== this.imageSwapToken) return;

      this.mainImage.src = src;
      if (trimmedSrcset) {
        this.mainImage.srcset = trimmedSrcset;
      } else {
        this.mainImage.removeAttribute('srcset');
      }
      if (mediaId) {
        this.mainImage.dataset.mediaId = String(mediaId);
        if (this.mediaZoomBtn) {
          this.mediaZoomBtn.dataset.mediaId = String(mediaId);
        }
      }

      return this.waitForImageElement(this.mainImage);
    }).then(() => {
      if (token !== this.imageSwapToken) return;

      requestAnimationFrame(() => {
        if (token === this.imageSwapToken) {
          this.mainImage.classList.remove('is-swapping');
        }
      });
    });
  }

  getVariantMedia(variant) {
    if (!variant || !this.product?.media?.length) return null;

    const featuredSrc = variant.featured_image?.src;
    if (!featuredSrc) return null;

    return (
      this.product.media.find((media) => {
        const mediaSrc = media.src || media.preview_image?.src;
        return mediaSrc && this.imageSrcMatches(mediaSrc, featuredSrc);
      }) || null
    );
  }

  findThumbForVariant(variant, thumbs) {
    if (!variant || !thumbs?.length) return null;

    const variantMedia = this.getVariantMedia(variant);
    if (variantMedia) {
      const byMediaId = thumbs.find((thumb) => thumb.dataset.mediaId === String(variantMedia.id));
      if (byMediaId) return byMediaId;
    }

    const featuredSrc = variant.featured_image?.src;
    if (featuredSrc) {
      return (
        thumbs.find((thumb) => this.imageSrcMatches(thumb.dataset.src, featuredSrc)) || null
      );
    }

    return null;
  }

  isPersonalisationSelected() {
    return Boolean(this.personaliseToggle?.checked);
  }

  mediaAltContainsEmb(alt) {
    return (alt || '').toUpperCase().includes('EMB');
  }

  findEmbThumb(thumbs) {
    if (!thumbs?.length) return null;

    return (
      Array.from(thumbs).find((thumb) => this.mediaAltContainsEmb(thumb.dataset.mediaAlt || '')) ||
      null
    );
  }

  findEmbMedia(colour) {
    if (!this.product?.media?.length) return null;

    const colourMatches = this.product.media.filter((media) =>
      this.mediaAltMatchesColour(media.alt, colour)
    );

    const scopedMatch = colourMatches.find((media) => this.mediaAltContainsEmb(media.alt));
    if (scopedMatch) return scopedMatch;

    return this.product.media.find((media) => this.mediaAltContainsEmb(media.alt)) || null;
  }

  findThumbForMedia(media, thumbs) {
    if (!media || !thumbs?.length) return null;

    return (
      Array.from(thumbs).find((thumb) => thumb.dataset.mediaId === String(media.id)) ||
      Array.from(thumbs).find((thumb) => {
        const mediaSrc = media.src || media.preview_image?.src;
        return mediaSrc && this.imageSrcMatches(thumb.dataset.src, mediaSrc);
      }) ||
      null
    );
  }

  activateEmbThumb(visibleThumbs, allThumbs) {
    const embThumb =
      this.findEmbThumb(visibleThumbs) || this.findEmbThumb(allThumbs);
    if (!embThumb) return false;

    embThumb.click();
    return true;
  }

  swapToEmbMedia(colour) {
    const embMedia = this.findEmbMedia(colour);
    if (!embMedia) return false;

    const src = embMedia.src || embMedia.preview_image?.src;
    if (!src) return false;

    const embThumb = this.findThumbForMedia(embMedia, this.thumbs);
    this.swapMainImage({
      src,
      srcset: embThumb?.dataset.srcset,
      mediaId: embMedia.id,
    });

    this.thumbs.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.mediaId === String(embMedia.id));
    });

    return true;
  }

  mediaAltMatchesColour(alt, colour) {
    if (!colour) return true;

    const normalizedAlt = (alt || '').trim().toLowerCase();
    const normalizedColour = colour.trim().toLowerCase();

    if (!normalizedAlt) return false;
    if (normalizedAlt === normalizedColour) return true;

    if (normalizedAlt.startsWith(`${normalizedColour} `) || normalizedAlt.startsWith(`${normalizedColour}-`)) {
      return true;
    }

    return false;
  }

  filterGalleryByColour(colour) {
    if (!this.thumbs.length) return 0;

    let visibleCount = 0;

    this.thumbs.forEach((thumb) => {
      const matches = this.mediaAltMatchesColour(thumb.dataset.mediaAlt || '', colour);
      thumb.classList.toggle('is-filtered-out', !matches);
      thumb.toggleAttribute('hidden', !matches);
      if (matches) visibleCount += 1;
    });

    return visibleCount;
  }

  activateVisibleThumb(variant) {
    const allThumbs = Array.from(this.thumbs);
    if (!allThumbs.length) return;

    let visibleThumbs = allThumbs.filter((thumb) => !thumb.classList.contains('is-filtered-out'));

    if (!visibleThumbs.length) {
      allThumbs.forEach((thumb) => {
        thumb.classList.remove('is-filtered-out');
        thumb.removeAttribute('hidden');
      });
      visibleThumbs = allThumbs;
    }

    const variantThumb =
      this.findThumbForVariant(variant, visibleThumbs) || this.findThumbForVariant(variant, allThumbs);

    if (this.isPersonalisationSelected()) {
      if (this.activateEmbThumb(visibleThumbs, allThumbs)) {
        return;
      }
    }

    (variantThumb || visibleThumbs[0]).click();
  }

  findMediaByColour(colour) {
    if (!colour || !this.product?.media?.length) return null;

    return this.product.media.find((media) => this.mediaAltMatchesColour(media.alt, colour)) || null;
  }

  getVariantImageSrc(variant, colour) {
    const swatch = this.getSwatchForColour(colour);
    if (swatch?.dataset.imageSrc) {
      return swatch.dataset.imageSrc;
    }

    const variantMedia = this.getVariantMedia(variant);
    if (variantMedia) {
      return variantMedia.src || variantMedia.preview_image?.src || '';
    }

    if (variant?.featured_image?.src) {
      const featuredSrc = variant.featured_image.src;
      return featuredSrc.startsWith('//') ? `https:${featuredSrc}` : featuredSrc;
    }

    const matchedMedia = this.findMediaByColour(colour);
    if (matchedMedia) {
      return matchedMedia.src || matchedMedia.preview_image?.src || '';
    }

    if (this.product?.featured_image) {
      return this.product.featured_image;
    }

    const firstMedia = this.product?.media?.[0];
    return firstMedia?.src || firstMedia?.preview_image?.src || '';
  }

  updateMainImageForVariant() {
    if (!this.mainImage) return;

    const colour = this.getSelectedColour();

    if (this.isPersonalisationSelected() && this.swapToEmbMedia(colour)) {
      return;
    }

    const swatch = this.getSwatchForColour(colour);
    const nextSrc = this.getVariantImageSrc(this.currentVariant, colour);
    if (!nextSrc) return;

    const variantMedia = this.getVariantMedia(this.currentVariant);
    const mediaId =
      variantMedia?.id || this.currentVariant?.featured_image?.id || this.mainImage.dataset.mediaId;

    this.swapMainImage({
      src: nextSrc,
      srcset: swatch?.dataset.imageSrcset,
      mediaId,
    });
  }

  updateGalleryForVariant() {
    const colour = this.getSelectedColour();
    this.filterGalleryByColour(colour);

    if (this.thumbs.length) {
      this.activateVisibleThumb(this.currentVariant);
      return;
    }

    this.updateMainImageForVariant();
  }

  updateUrl() {
    if (this.isQuickView() || !this.currentVariant || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  initPersonalisationFromVariant() {
    if (!this.personaliseToggle) return;

    const optionName = this.getPersonalisationOptionName();
    if (!optionName) return;

    const { yes } = this.getPersonalisationValues(optionName);
    const isPersonalised = this.selectedOptions[optionName] === yes;

    this.personaliseToggle.checked = isPersonalised;
    this.personaliseFields.classList.toggle('is-visible', isPersonalised);
  }

  bindPersonalisation() {
    if (!this.personaliseToggle || !this.personaliseFields) return;

    this.initPersonalisationFromVariant();
    this.updateSwatchAvailability();

    this.personaliseToggle.addEventListener('change', () => {
      this.personaliseFields.classList.toggle('is-visible', this.personaliseToggle.checked);
      if (!this.personaliseToggle.checked) {
        this.personaliseInputs.forEach((input) => {
          input.value = '';
        });
        if (this.personaliseTerms) {
          this.personaliseTerms.checked = false;
        }
      }

      const optionName = this.getPersonalisationOptionName();
      if (optionName) {
        const { no, yes } = this.getPersonalisationValues(optionName);
        this.selectedOptions[optionName] = this.personaliseToggle.checked ? yes : no;
      }

      this.refreshVariantSelection();
    });
  }

  hasPersonalisationText() {
    return Array.from(this.personaliseInputs).some((input) => input.value.trim() !== '');
  }

  getQuantityMin() {
    const min = parseInt(this.quantityInput?.min, 10);
    return Number.isFinite(min) && min > 0 ? min : 1;
  }

  getQuantityValue() {
    const value = parseInt(this.quantityInput?.value, 10);
    return Number.isFinite(value) && value >= this.getQuantityMin() ? value : this.getQuantityMin();
  }

  setQuantityValue(value) {
    if (!this.quantityInput) return;
    const min = this.getQuantityMin();
    const next = Math.max(min, value);
    this.quantityInput.value = String(next);
    this.updateQuantityButtons();
  }

  updateQuantityButtons() {
    if (!this.quantityInput) return;
    const value = this.getQuantityValue();
    if (this.quantityMinus) {
      this.quantityMinus.disabled = value <= this.getQuantityMin();
    }
  }

  bindDescription() {
    const wrap = this.root.querySelector('[data-bbs-description-wrap]');
    if (!wrap) return;

    const description = wrap.querySelector('[data-bbs-description]');
    const toggle = wrap.querySelector('[data-bbs-description-toggle]');
    const label = wrap.querySelector('[data-bbs-description-toggle-label]');
    if (!description || !toggle || !label) return;

    const showMore = toggle.dataset.showMore || 'Show more';
    const showLess = toggle.dataset.showLess || 'Show less';

    const updateToggleVisibility = () => {
      if (!description.classList.contains('is-clamped')) {
        toggle.hidden = false;
        return;
      }

      toggle.hidden = description.scrollHeight <= description.clientHeight + 1;
    };

    updateToggleVisibility();
    window.addEventListener('resize', updateToggleVisibility);

    toggle.addEventListener('click', () => {
      const isCollapsed = description.classList.toggle('is-clamped');
      const isExpanded = !isCollapsed;

      toggle.setAttribute('aria-expanded', String(isExpanded));
      label.textContent = isExpanded ? showLess : showMore;

      if (isExpanded) {
        toggle.hidden = false;
      } else {
        updateToggleVisibility();
      }
    });
  }

  bindQuantity() {
    if (!this.quantityInput) return;

    this.quantityMinus?.addEventListener('click', () => {
      this.setQuantityValue(this.getQuantityValue() - 1);
    });

    this.quantityPlus?.addEventListener('click', () => {
      this.setQuantityValue(this.getQuantityValue() + 1);
    });

    this.quantityInput.addEventListener('change', () => {
      this.setQuantityValue(this.getQuantityValue());
    });

    this.quantityInput.addEventListener('blur', () => {
      this.setQuantityValue(this.getQuantityValue());
    });

    this.updateQuantityButtons();
  }

  bindForm() {
    if (!this.form) return;

    this.form.addEventListener(
      'submit',
      (event) => {
        this.clearFormError();

        if (!this.currentVariant || !this.findVariant()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showFormError('This combination is not available. Please choose another option.');
          return;
        }

        const needsPersonalisation = this.personaliseFields?.classList.contains('is-visible');
        const hasText = this.hasPersonalisationText();

        if (needsPersonalisation && hasText && this.personaliseTerms && !this.personaliseTerms.checked) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showFormError('Please accept the personalisation terms before adding to basket.');
          return;
        }

        if (needsPersonalisation && !hasText) {
          this.personaliseInputs.forEach((input) => {
            input.removeAttribute('name');
          });
        } else if (needsPersonalisation) {
          this.personaliseInputs.forEach((input) => {
            if (input.value.trim() !== '') {
              input.setAttribute('name', input.dataset.propertyName);
            } else {
              input.removeAttribute('name');
            }
          });
        }
      },
      true
    );
  }

  clearFormError() {
    const errorEl = this.form.querySelector('[data-bbs-form-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  showFormError(message) {
    let errorEl = this.form.querySelector('[data-bbs-form-error]');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'bbs-product-form__error';
      errorEl.dataset.bbsFormError = '';
      this.form.querySelector('.bbs-product__buttons')?.before(errorEl);
    }
    errorEl.hidden = false;
    errorEl.textContent = message;
  }

  bindShare() {
    const shareRoot = this.root.querySelector('[data-bbs-share]');
    if (!shareRoot) return;

    const pageUrl = encodeURIComponent(window.location.href);
    const pageTitle = encodeURIComponent(document.title);

    shareRoot.querySelectorAll('[data-bbs-share-action]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const action = button.dataset.bbsShareAction;

        switch (action) {
          case 'facebook':
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`, '_blank', 'noopener,noreferrer,width=600,height=500');
            break;
          case 'x':
            window.open(`https://twitter.com/intent/tweet?url=${pageUrl}&text=${pageTitle}`, '_blank', 'noopener,noreferrer,width=600,height=500');
            break;
          case 'pinterest':
            window.open(`https://pinterest.com/pin/create/button/?url=${pageUrl}&description=${pageTitle}`, '_blank', 'noopener,noreferrer,width=600,height=500');
            break;
          case 'email':
            window.location.href = `mailto:?subject=${pageTitle}&body=${pageUrl}`;
            break;
          case 'instagram':
            this.copyToClipboard(decodeURIComponent(window.location.href));
            this.showShareToast('Link copied — paste into Instagram');
            break;
          case 'copy':
            this.copyToClipboard(decodeURIComponent(window.location.href));
            this.showShareToast('Link copied to clipboard');
            break;
          default:
            break;
        }
      });
    });
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
  }

  showShareToast(message) {
    if (!this.shareToast) return;
    this.shareToast.textContent = message;
    this.shareToast.classList.add('is-visible');
    window.clearTimeout(this.shareToastTimer);
    this.shareToastTimer = window.setTimeout(() => {
      this.shareToast.classList.remove('is-visible');
    }, 2400);
  }

  trackRecentlyViewed() {
    if (!this.product) return;

    const key = 'bbs-recently-viewed';
    const entry = {
      id: this.product.id,
      handle: this.product.handle,
      title: this.product.title,
      url: `/products/${this.product.handle}`,
      image: this.product.featured_image || this.currentVariant?.featured_image?.src || '',
      price: this.product.price,
    };

    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      items = [];
    }

    items = items.filter((item) => String(item.id) !== String(this.product.id));
    items.unshift(entry);
    items = items.slice(0, 6);
    localStorage.setItem(key, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('bbs:recently-viewed-updated'));
  }
}

class BBSRecentlyViewed {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-bbs-recently-grid]');
    this.currentId = root.dataset.currentProductId;
    this.hideWhenEmpty = root.dataset.hideWhenEmpty !== 'false';
    this.render();
    document.addEventListener('bbs:recently-viewed-updated', () => this.render());
  }

  setSectionVisible(visible) {
    this.root.hidden = !visible;
    const section = this.root.closest('.shopify-section');
    if (section) section.hidden = !visible;
  }

  render() {
    if (!this.grid) return;

    this.filterPublishedItems()
      .then((items) => {
        items = items.filter((item) => String(item.id) !== String(this.currentId)).slice(0, 5);

        if (items.length === 0) {
          if (this.hideWhenEmpty) {
            this.setSectionVisible(false);
          } else {
            this.setSectionVisible(true);
            this.grid.innerHTML =
              '<li class="bbs-recently-viewed__empty"><p class="bbs-product__inside-desc">No recently viewed products yet.</p></li>';
          }
          return;
        }

        this.setSectionVisible(true);

        const formatMoney = (cents) => {
          if (window.Shopify?.formatMoney) {
            return window.Shopify.formatMoney(cents);
          }
          return `£${(cents / 100).toFixed(2)}`;
        };

        this.grid.innerHTML = items
          .map(
            (item) => `
      <li>
        <a href="${item.url}" class="bbs-recent-card">
          <div class="bbs-recent-card__media">
            ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="200" height="250">` : ''}
          </div>
          <p class="bbs-recent-card__title">${item.title}</p>
          <p class="bbs-recent-card__price">${formatMoney(item.price)}</p>
        </a>
      </li>`
          )
          .join('');
      })
      .catch((error) => {
        console.error('BBS recently viewed render failed', error);
        this.setSectionVisible(false);
      });
  }

  async filterPublishedItems() {
    const key = 'bbs-recently-viewed';
    let items = [];

    try {
      items = JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      items = [];
    }

    if (!items.length) return [];

    const validated = await Promise.all(
      items.map(async (item) => {
        if (!item?.handle && !item?.url) return null;

        const productUrl = item.url || `/products/${item.handle}`;

        try {
          const response = await fetch(`${productUrl}.js`);
          if (!response.ok) return null;

          const data = await response.json();
          return {
            id: data.id,
            handle: data.handle,
            title: data.title,
            url: `/products/${data.handle}`,
            image: data.featured_image || data.images?.[0] || item.image || '',
            price: data.price,
          };
        } catch (error) {
          return null;
        }
      })
    );

    const published = validated.filter(Boolean);

    if (published.length !== items.length) {
      localStorage.setItem(key, JSON.stringify(published));
    }

    return published;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bbs-product-page]').forEach((root) => {
    new BBSProductPage(root);
  });

  document.querySelectorAll('[data-bbs-recently-viewed]').forEach((root) => {
    new BBSRecentlyViewed(root);
  });

  document.querySelectorAll('.shopify-section[class*="bbs-section-"]').forEach((section) => {
    if (section.hidden) return;
    const hasContent = section.querySelector('[class*="bbs-"], [data-bbs-]');
    if (!hasContent) section.hidden = true;
  });
});

window.BBSProductPage = BBSProductPage;
